import React from "react";
import { useTheme } from "@mui/material/styles";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useLocation } from "react-router-dom";

import PublicPageLayout from "./PublicPageLayout";
import { getPublicSiteSx } from "./publicSiteStyles";
import { useAuthState } from "../app/AuthStateContext";
import {
  LEAVER_PENALTY_APPEAL_CATEGORY,
  getSupportTicketInitialState,
} from "../support/leaverPenaltyAppeal";
import { submitSupportTicket } from "../support/submitSupportTicket";

const CATEGORIES = [
  { value: "account", label: "Account" },
  { value: "billing", label: "Billing & Memberships" },
  { value: "matchmaking", label: "Matchmaking" },
  { value: "reputation_appeal", label: "Reputation Appeal" },
  { value: "report", label: "Player Report Follow-up" },
  { value: "bug", label: "Bug Report" },
  { value: "other", label: "Other" },
];

export default function SupportPage() {
  const theme = useTheme();
  const sx = React.useMemo(() => getPublicSiteSx(theme), [theme]);
  const { user } = useAuthState();
  const location = useLocation();
  const supportTicketInitialState = React.useMemo(
    () =>
      getSupportTicketInitialState({
        userEmail: user?.email || "",
        search: location.search,
        state: location.state,
      }),
    [location.search, location.state, user?.email]
  );

  const [form, setForm] = React.useState(supportTicketInitialState.form);
  const [appeal, setAppeal] = React.useState(supportTicketInitialState.appeal);
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    setForm(supportTicketInitialState.form);
    setAppeal(supportTicketInitialState.appeal);
    setResult(null);
    setError(null);
  }, [location.search, location.state]);

  React.useEffect(() => {
    if (!user?.email) {
      return;
    }

    setForm((prev) =>
      prev.email === user.email ? prev : { ...prev, email: user.email }
    );
  }, [user?.email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        name === "category" && appeal ? LEAVER_PENALTY_APPEAL_CATEGORY : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.subject.trim() || !form.description.trim()) {
      setError("Please fill in the subject and description.");
      return;
    }
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError("Please provide a valid email address.");
      return;
    }

    setSubmitting(true);
    try {
      const data = await submitSupportTicket({
        category: form.category,
        email: form.email,
        subject: form.subject,
        description: form.description,
        appeal,
      });
      if (data.ok) {
        setResult(data.ticket);
      } else {
        setError(data.message);
      }
    } catch {
      setError("Could not reach the server. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <PublicPageLayout
        eyebrow="Support"
        title="Ticket submitted."
        description="We received your request and will get back to you as soon as possible."
        mapBackground
      >
        <Box sx={sx.section}>
          <Box sx={{ ...sx.card, maxWidth: 560, textAlign: "center", mx: "auto" }}>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 48, color: "#E8A33D", mb: 2 }}
            />
            <Typography sx={{ ...sx.cardTitle, mb: 1 }}>
              Your ticket number is {result.ticketNumber}
            </Typography>
            <Typography sx={sx.cardBody}>
              Keep this number for reference. If you are logged in, you can track
              your tickets from your account.
            </Typography>
          </Box>
        </Box>
      </PublicPageLayout>
    );
  }

  return (
    <PublicPageLayout
      eyebrow="Support"
      title="How can we help?"
      description="Submit a support request below and we will follow up as soon as we can. If you are logged in, the ticket will be linked to your account."
      mapBackground
    >
      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>Tell us what blocked you.</Typography>
        </Box>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            ...sx.card,
            height: "auto",
            overflow: "visible",
            maxWidth: 640,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            "& .MuiInputLabel-root": {
              color: "rgba(255,255,255,0.62)",
            },
            "& .MuiInputBase-root": {
              color: "#ffffff",
              backgroundColor: "rgba(255,255,255,0.025)",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.16)",
            },
            "& .MuiFormHelperText-root": {
              color: "rgba(255,255,255,0.48)",
            },
          }}
        >
          <FormControl fullWidth>
            <InputLabel id="support-category-label">Category</InputLabel>
            <Select
              labelId="support-category-label"
              id="support-category"
              name="category"
              value={form.category}
              label="Category"
              onChange={handleChange}
              disabled={!!appeal}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            disabled={!!user?.email}
            helperText={user?.email ? "Using your account email." : "We will reply to this address."}
          />

          <TextField
            fullWidth
            label="Subject"
            name="subject"
            value={form.subject}
            onChange={handleChange}
            inputProps={{ maxLength: 200 }}
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={form.description}
            onChange={handleChange}
            multiline
            minRows={5}
            inputProps={{ maxLength: 5000 }}
            helperText="Describe your issue in as much detail as you can."
          />

          {appeal && (
            <Typography sx={{ color: "#b5b1b3", fontSize: "0.85rem", lineHeight: 1.5 }}>
              This ticket will include your leaver penalty match context automatically.
            </Typography>
          )}

          {error && (
            <Typography sx={{ color: "#f44336", fontSize: "0.9rem" }}>
              {error}
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{ ...sx.primaryButton, alignSelf: "flex-start", minWidth: 160 }}
          >
            {submitting ? <CircularProgress size={22} sx={{ color: "#0d0d0d" }} /> : "Submit ticket"}
          </Button>
        </Box>
      </Box>
    </PublicPageLayout>
  );
}
